import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import { BehaviorSubject, filter, Subscription, take, tap } from 'rxjs';

import { makeMap } from '@shared/core';
import { EditAccountUserRequest } from '@shared/account/models';
import { UserAccountRole, UserLocationRole } from '@shared/user/enums';
import { AssignLocationComponent } from '@tablesready/assign-location';
import {
  ConfirmationModalComponent,
  ConfirmationModalResult,
  LocationInfo,
  LocationUserWithLocationName,
  UserWithAccount,
} from '@tablesready/common-ui';
import { FrontendModalService, FrontendModalSize } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';

interface LocationExtended extends LocationUserWithLocationName {
  locationEnabled: boolean;
}

@Component({
  selector: 'tablesready-edit-user-access',
  templateUrl: './edit-user-access.component.html',
  styleUrls: ['./edit-user-access.component.scss'],
})
export class EditUserAccessComponent implements OnInit, OnDestroy {
  @ViewChild('adminAccessCheckbox') adminAccessCheckbox?: ElementRef;
  @ViewChild('contentDivRef') contentDivRef?: ElementRef;
  @ViewChild('topContent') topContent?: ElementRef;

  @Input() user?: UserWithAccount;
  @Input() locations: LocationInfo[] = [];
  @Input() openedFromAdminPanel = false;
  @Input() accountName?: string;
  @Input() size = FrontendModalSize.default;
  @Input() onUserUpdate?: (
    instance: EditUserAccessComponent,
    accountUserId: string,
    data: EditAccountUserRequest,
  ) => void;

  @Input() onNavigationToLocationPage?: (locationId: string) => void;
  @Input() onClose?: () => void;
  @Input() onNavigatedToLocations?: () => void;
  @Input() onRemoveFromLocation?: (
    instance: EditUserAccessComponent,
    locationId: string,
    locationUserId: string,
  ) => void;

  icon = AppIcon;
  color = AppIconColor;
  locationUsers: LocationExtended[] = [];
  locationsToDisplay: LocationExtended[] = [];
  userLocationsMap: Record<string, LocationUserWithLocationName> = {};
  userRoles = [
    {
      name: 'User',
      value: UserLocationRole.user,
    },
    {
      name: 'Manager',
      value: UserLocationRole.manager,
    },
  ];
  private readonly getDataSource = new BehaviorSubject(true);
  private readonly subscriptions = new Subscription();

  constructor(private readonly modalController: FrontendModalService) {}

  refreshData(): void {
    this.getDataSource.next(true);
  }

  ngOnInit(): void {
    const dataSubscription = this.getDataSource
      .pipe(
        tap(() => {
          if (!this.user) {
            return;
          }

          const locationEnabledMap: Record<string, boolean> = {};
          this.locations.forEach((loc) => {
            locationEnabledMap[loc.id] = loc.enabled;
          });

          this.locationUsers = this.user.locationUsers.map(user => ({
            ...user,
            locationEnabled: locationEnabledMap[user.locationId],
          }));

          this.locationsToDisplay = this.locationUsers;
          this.userLocationsMap = makeMap(this.locationUsers, 'locationId');
        }),
      )
      .subscribe();

    this.subscriptions.add(dataSubscription);
  }

  updateUserRole(userLocation: LocationUserWithLocationName, newRole: string): void {
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        content: `Are you sure you want to change ${this.user?.name} role to: ${newRole}?`,
        closeBtnText: 'Cancel',
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        tap(() => {
          const locationsToUpdate = [];

          for (const locationUser of this.user.locationUsers) {
            if (locationUser.locationId === userLocation.locationId) {
              locationsToUpdate.push({
                id: userLocation.locationId,
                role: newRole,
              });
              continue;
            }

            locationsToUpdate.push({
              id: locationUser.locationId,
              role: locationUser.role,
            });
          }

          const data: EditAccountUserRequest = {
            adminAccess: this.isAdmin,
            locations: locationsToUpdate,
          };
          this.userLocationsMap[userLocation.locationId].role = newRole as UserLocationRole;

          if (this.onUserUpdate) {
            this.onUserUpdate(this, String(this.user?.id), data);
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  filterLocations(queryString: string): void {
    if (queryString === '') {
      this.locationsToDisplay = this.locationUsers;

      return;
    }

    this.locationsToDisplay = this.locationUsers.filter(locationUser => locationUser.locationName.toLowerCase().includes(queryString));
  }

  removeFromLocation(locationUser: LocationUserWithLocationName): void {
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        title: 'Delete user',
        content: `You are about to delete ${this.user?.name} from ${locationUser.locationName}. Do you want to continue?`,
        closeBtnText: 'Decline',
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        tap(() => {
          if (!this.onRemoveFromLocation) {
            return;
          }

          this.onRemoveFromLocation(this, locationUser.locationId, locationUser.id);
        }),
      )
      .subscribe();
  }

  changeAccountRole(checkbox: HTMLInputElement): void {
    const checked = checkbox.checked;
    const content = checked ? `You are about to set administrator access for ${this.user?.name}. Do you want to continue?` : `You are about to remove administrator rights from ${this.user?.name}. Do you want to continue?`;
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        title: 'Administrator access',
        content,
        closeBtnText: 'Decline',
      },
    })
      .pipe(
        take(1),
        tap((res) => {
          if (!res?.response) {
            checkbox.checked = !checkbox.checked;
          }
        }),
        filter(res => res?.response),
        tap(() => {
          const data: EditAccountUserRequest = {
            adminAccess: checked,
            locations: checked ? null : [],
          };
          if (this.onUserUpdate) {
            this.onUserUpdate(this, String(this.user?.id), data);
          }
        }),
      )
      .subscribe();
  }

  assignLocations(): void {
    this.modalController.create<AssignLocationComponent, EditAccountUserRequest>(
      AssignLocationComponent,
      {
        user: this.user,
        locations: this.locations,
      },
      this.size,
    )
      .pipe(
        take(1),
        filter(data => !!data),
        tap((data: EditAccountUserRequest) => {
          if (this.onUserUpdate) {
            this.onUserUpdate(this, String(this.user?.id), data);
          }
        }),
      )
      .subscribe();
  }

  closeAll(): void {
    this.close();
    if (this.onClose) {
      this.onClose();
    }
  }

  close(): void {
    this.modalController.dismiss();
  }

  navigateToLocationWaitlist(locationId: string): void {
    if (this.onNavigationToLocationPage) {
      this.onNavigationToLocationPage(locationId);
    }
    this.close();
  }

  navigateToLocationsPage(): void {
    if (this.onNavigatedToLocations) {
      this.onNavigatedToLocations();
    }
    this.close();
  }

  get isAdmin(): boolean {
    return this.user?.role === UserAccountRole.admin;
  }

  get maxHeight(): number {
    return this.contentDivRef?.nativeElement.offsetHeight - this.topContent?.nativeElement.offsetHeight - 16;
  }
}
